/*
 * Kona ad landing page behavior: discount-cookie gating of the discounted
 * pricing presentation, UTM capture, grind toggle, smooth scroll, sticky
 * mobile CTA, UTM injection into the add-to-cart form, and deep-link
 * handling (anchor scroll + optional grind pre-selection) for email traffic.
 */
class BhKonaLanding extends HTMLElement {
  connectedCallback() {
    this.applyDiscountState();
    this.captureUtm();
    this.bindSmoothScroll();
    this.bindGrindToggle();
    this.bindStickyCta();
    this.bindUtmInjection();
    this.applyDeepLink();
  }

  applyDiscountState() {
    // Shopify sets a discount_code cookie when a /discount/CODE link is visited;
    // only then is the discounted presentation truthful.
    const discountCookie = document.cookie.split(';').find((cookie) => cookie.trim().startsWith('discount_code='));
    if (discountCookie && discountCookie.split('=').slice(1).join('=').trim()) {
      this.classList.add('kona-landing--discount-active');
    }
  }

  captureUtm() {
    const params = new URLSearchParams(window.location.search);
    const utmData = {};
    let hasUtm = false;

    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
      const value = params.get(key);
      if (value) {
        utmData[key] = value;
        hasUtm = true;
      }
    }

    if (hasUtm) {
      document.cookie = `_bh_utm=${encodeURIComponent(JSON.stringify(utmData))};path=/;max-age=2592000;SameSite=Lax`;
    }
  }

  bindSmoothScroll() {
    for (const link of this.querySelectorAll('a[href^="#"]')) {
      link.addEventListener('click', (event) => {
        const target = document.getElementById(link.getAttribute('href').slice(1));
        if (target) {
          event.preventDefault();
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }
  }

  bindGrindToggle() {
    for (const button of this.querySelectorAll('[data-grind-button]')) {
      button.addEventListener('click', () => {
        const card = button.closest('[data-card]');
        if (!card) return;

        for (const sibling of card.querySelectorAll('[data-grind-button]')) {
          sibling.classList.toggle('active', sibling === button);
        }

        for (const form of card.querySelectorAll('[data-grind-form]')) {
          form.hidden = form.dataset.grind !== button.dataset.grind;
        }
      });
    }
  }

  applyDeepLink() {
    const hash = window.location.hash.slice(1);
    if (!hash) return;

    const card = this.querySelector(`[data-card="${hash}"]`);
    if (!card) return;

    // Optional ?grind=ground (or ?grind=whole-bean) pre-selects the toggle before scrolling.
    const params = new URLSearchParams(window.location.search);
    const grind = params.get('grind');
    if (grind) {
      const button = card.querySelector(`[data-grind-button][data-grind="${grind}"]`);
      if (button) button.click();
    }

    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  bindStickyCta() {
    const stickyCta = this.querySelector('[data-sticky-cta]');
    const hero = this.querySelector('[data-hero]');
    const products = this.querySelector('[data-products]');
    if (!stickyCta || !hero) return;

    const update = () => {
      if (window.innerWidth > 768) {
        stickyCta.style.display = 'none';
        return;
      }
      const heroBottom = hero.getBoundingClientRect().bottom;
      const productTop = products ? products.getBoundingClientRect().top : Infinity;
      const productBottom = products ? products.getBoundingClientRect().bottom : Infinity;
      const show = heroBottom < 0 && (productTop > window.innerHeight || productBottom < 0);
      stickyCta.style.display = show ? 'block' : 'none';
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  bindUtmInjection() {
    for (const form of this.querySelectorAll('[data-products] form')) {
      form.addEventListener('submit', () => {
        // Attach captured UTM parameters as cart attributes.
        const utmCookie = document.cookie.split(';').find((cookie) => cookie.trim().startsWith('_bh_utm='));
        if (utmCookie) {
          try {
            const utmData = JSON.parse(decodeURIComponent(utmCookie.split('=').slice(1).join('=')));
            for (const [key, value] of Object.entries(utmData)) {
              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = `attributes[${key}]`;
              input.value = value;
              form.appendChild(input);
            }
          } catch {
            /* malformed cookie */
          }
        }
      });
    }
  }
}

customElements.define('bh-kona-landing', BhKonaLanding);
